/**
 * AdminConfirmationForm — reauthenticate and type an exact phrase.
 *
 * TCA-26100804 closing audit. Three destructive actions each asked for an
 * administrator username, a password and a typed confirmation phrase, and each
 * wrote that out separately: rebuilding the protected cache, deleting the
 * recovery quarantine, and rebuilding order data. Three copies of one rule is
 * three places for a later change to miss one, and nothing made them agree —
 * each was asserted against its own copy, so a gate could be dropped from one
 * without a single test failing.
 *
 * The rule lives here now, in `useAdminConfirmation`: a confirm is possible only
 * with a non-blank username, a password, and the phrase typed exactly. Callers
 * supply their own wording, labels, styling and extra conditions; none of them
 * can relax the rule itself.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Owns the three fields and the one condition that unlocks the confirm button.
 * `credentials` is shaped for the desktop bridge calls that consume it.
 */
export function useAdminConfirmation(phrase) {
  const version = useRef(0);
  const [username, updateUsername] = useState('');
  const setUsername = value => { version.current++; updateUsername(value); };
  const [password, updatePassword] = useState('');
  const setPassword = value => { version.current++; updatePassword(value); };
  const [confirmation, updateConfirmation] = useState('');
  const setConfirmation = value => { version.current++; updateConfirmation(value); };

  // The password is deliberately not trimmed: leading or trailing spaces are
  // part of a password, and the phrase must match exactly.
  const complete = Boolean(username.trim()) && Boolean(password) && confirmation === phrase;

  const reset = useCallback(() => {
    version.current++;
    updatePassword('');
    updateConfirmation('');
  }, []);

  const credentials = useMemo(
    () => ({ username, password, confirmation }),
    [username, password, confirmation],
  );

  return {
    capture: () => {
      const submitted = version.current;
      return () => submitted === version.current;
    },
    phrase,
    username,
    setUsername,
    password,
    setPassword,
    confirmation,
    setConfirmation,
    complete,
    reset,
    credentials,
  };
}

export default function AdminConfirmationForm({
  fields,
  testIdPrefix,
  instruction,
  confirmLabel,
  busyLabel,
  busy = false,
  // An extra reason this particular action cannot run. It can only ever add a
  // condition; it cannot satisfy the confirmation rule.
  disabled = false,
  onConfirm,
  onCancel = null,
  className = 'mt-3 space-y-3',
  instructionClassName = 'text-xs text-stone-700',
}) {
  return (
    <div className={className} data-testid={`${testIdPrefix}-form`}>
      <p className={instructionClassName}>{instruction}</p>
      <div className="grid gap-2 md:grid-cols-2">
        <Input
          value={fields.username}
          onChange={(event) => fields.setUsername(event.target.value)}
          placeholder="Administrator username"
          autoComplete="username"
          data-testid={`${testIdPrefix}-username`}
        />
        <Input
          type="password"
          value={fields.password}
          onChange={(event) => fields.setPassword(event.target.value)}
          placeholder="Administrator password"
          autoComplete="current-password"
          data-testid={`${testIdPrefix}-password`}
        />
      </div>
      <Input
        value={fields.confirmation}
        onChange={(event) => fields.setConfirmation(event.target.value)}
        placeholder={fields.phrase}
        data-testid={`${testIdPrefix}-confirmation`}
      />
      <div className="flex gap-2">
        <Button
          variant="destructive"
          size="sm"
          disabled={busy || disabled || !fields.complete}
          onClick={onConfirm}
          data-testid={`${testIdPrefix}-confirm`}
        >
          {busy ? busyLabel : confirmLabel}
        </Button>
        {onCancel && (
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
